import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Rocket, Zap, Shield, Trophy, Flame, Target, GraduationCap, Crown, Sparkles, Lock } from "lucide-react";
import ShareButtons from "@/components/ShareButtons";
import { api } from "@/lib/api";
import { webAppUrl } from "@/lib/platform";

const ICONS = {
  rocket: Rocket,
  zap: Zap,
  shield: Shield,
  trophy: Trophy,
  flame: Flame,
  target: Target,
  "graduation-cap": GraduationCap,
  crown: Crown,
  sparkles: Sparkles,
};

const TIER_STYLES = {
  bronze: {
    earned: "from-amber-700 to-orange-600 text-white shadow-amber-200",
    ring: "ring-amber-300",
    label: "text-amber-700",
  },
  silver: {
    earned: "from-slate-400 to-slate-600 text-white shadow-slate-200",
    ring: "ring-slate-300",
    label: "text-slate-600",
  },
  gold: {
    earned: "from-amber-400 to-yellow-500 text-white shadow-amber-200",
    ring: "ring-amber-300",
    label: "text-amber-600",
  },
};

export function BadgeMedal({ badge, size = "md", testId }) {
  const Icon = ICONS[badge.icon] || Sparkles;
  const tier = TIER_STYLES[badge.tier] || TIER_STYLES.bronze;
  const dims = size === "sm" ? "h-10 w-10" : size === "lg" ? "h-20 w-20" : "h-14 w-14";
  const iconSize = size === "sm" ? "w-5 h-5" : size === "lg" ? "w-10 h-10" : "w-7 h-7";
  const earned = badge.earned !== false; // default earned when not specified

  if (!earned) {
    return (
      <div className={`${dims} rounded-full grid place-items-center bg-slate-100 text-slate-300 border-2 border-dashed border-slate-200`} data-testid={testId}>
        <Lock className={iconSize} strokeWidth={1.5} />
      </div>
    );
  }
  return (
    <div className={`${dims} rounded-full grid place-items-center bg-gradient-to-br ${tier.earned} shadow-md ring-2 ring-offset-2 ${tier.ring}`} data-testid={testId}>
      <Icon className={iconSize} strokeWidth={1.75} />
    </div>
  );
}

export function BadgeWithLabel({ badge, size = "md" }) {
  const tier = TIER_STYLES[badge.tier] || TIER_STYLES.bronze;
  const earned = badge.earned !== false;

  const getShareUrl = async () => {
    const r = await api.post(`/student/share/badge/${badge.id}`);
    return {
      url: `${webAppUrl()}/share/badge/${r.data.share_token}`,
      newly_awarded_badge: r.data.newly_awarded_badge,
    };
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex flex-col items-center gap-2 cursor-default" data-testid={`badge-${badge.id}`}>
            <BadgeMedal badge={badge} size={size} testId={`badge-medal-${badge.id}`} />
            <div className="text-center">
              <div className={`text-sm font-display font-bold ${badge.earned === false ? "text-slate-400" : "text-slate-900 dark:text-slate-100"}`}>{badge.name}</div>
              <div className={`text-[10px] uppercase tracking-[0.18em] mt-0.5 ${badge.earned === false ? "text-slate-400" : tier.label}`}>{badge.tier}</div>
            </div>
            {earned && (
              <ShareButtons
                getShareUrl={getShareUrl}
                defaultText={`I just unlocked "${badge.name}" on PrepMaster! 🏆`}
                variant="compact"
                testIdPrefix={`share-badge-${badge.id}`}
                className="mt-1"
              />
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent>{badge.description}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
